import Link from "next/link";
import useSWRInfinite from "swr/infinite";

import { useCallback, useMemo, useState } from "react";
import Popover from "react-bootstrap/Popover";
import InfiniteScroll from "react-swr-infinite-scroll";

import { isEmpty } from "lodash";
import { twMerge } from "tailwind-merge";

import { faCaretDown } from "@fortawesome/free-solid-svg-icons/faCaretDown";
import { faCaretUp } from "@fortawesome/free-solid-svg-icons/faCaretUp";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";

import AddToWatchlistButton from "components/AddToWatchlistButton";
import { Loader } from "components/customElements";
import EmptyState from "components/EmptyState";
import EntityAnalyticsChart from "components/EntityAnalyticsChart";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import StickyOverlayTrigger from "components/StickyOverlayTrigger";
import Table from "components/Table";
import { CUSTOM_OPTION } from "components/ToolDropdownSelector";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";

import Filter from "./Filters";

const COL_COLORS = [
  "bg-[#FFFFFF]",
  "bg-[#D8EEDC]",
  "bg-[#8BCC97]",
  "bg-[#97D1A0]",
  "bg-[#74C282]",
  "bg-[#4DB364]",
];

const DESCRIPTIONS = [
  {
    name: "Value",
    descriptions: `Value indicates the Company's share price per unit of net assets compared to its peers. Latest Reported Price-to-Book`,
  },
  {
    name: "Dividend",
    descriptions: `Dividend indicates the Company's payout to shareholders (dividends) compared to its peers. Latest Reported Dividend Yield`,
  },
  {
    name: "Growth",
    descriptions: `Growth indicates the Company's growth in earnings compared to its peers. 3yr Earning-per-share CAGR`,
  },
  {
    name: "Resilience",
    descriptions: `Resilience indicates the Company's profitability and ability to meet debt obligations as compared to its peers. Latest Reported Net-Debt to Equity and LTM Net Income Margin`,
  },
  {
    name: "Momentum",
    descriptions: `Momentum indicates the growth in the Company's Market Capitalisation compared to its peers. Market Capitalisation Growth`,
  },
];

const description = DESCRIPTIONS.map((item) => {
  return (
    <p className="mb-0 text-sm" key={item.name}>
      <b>{item.name} : </b>
      {item.descriptions}
    </p>
  );
});

const smartScoreScreenerUrl = (page: any, options: any) => {
  const url = new ApiEndpoint(`/api/v2/entity-smart-scores`);
  url.searchParams.append("include", "entity");
  url.searchParams.append("page[number]", page);

  Object.keys(options).forEach((key) => {
    url.searchParams.append(key, options[key]);
  });

  return url.href;
};

export default function ToolSmartScoreScreener() {
  const [sort, setSort] = useState("-countryAndSectorScore,-marketCap");
  const [filters, setFilters] = useState<any>();

  const { data, isValidating, mutate, size, setSize } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData?.length === 0) return null;
      const options: any = {
        sort,
      };

      if (!isEmpty(filters)) {
        Object.keys(filters).forEach((key) => {
          if (filters[key] !== CUSTOM_OPTION) {
            options[`filter[${key}]`] = filters[key];
          }
        });
      }
      return smartScoreScreenerUrl(pageIndex + 1, options);
    },
    baseFetcher
  );

  const hasMore = data?.[0]?.meta?.totalPages > size;

  const renderCols = useCallback((value, records, isLast = false) => {
    return (
      <div
        className={twMerge(
          "w-full font-bold p-[10px] py-[8px] text-center",
          "border-0 border-b border-b-slate-200 border-solid",
          !isLast && "border-r border-r-slate-200",
          value && COL_COLORS[Math.floor(value) == 0 ? 1 : Math.floor(value)],
          !value && COL_COLORS[0]
        )}
      >
        {value || "-"}
      </div>
    );
  }, []);

  const renderNormalCols = useCallback((value, record, isHref) => {
    if (isHref) {
      return (
        <div className="w-full p-[10px] py-[8px] truncate border-0 border-b border-b-slate-200 border-solid">
          <StickyOverlayTrigger
            placement="right"
            overlay={
              <Popover
                className={twMerge(
                  "left-[-9999px]", // add intitial postion outisde of viewport to fix flickering
                  "bg-white shadow-lg rounded",
                  "min-w-[300px]",
                  "border border-solid border-gray-200",
                  "font-roboto",
                  "z-[1999]"
                )}
              >
                <Popover.Body>
                  <div className="w-full flex justify-between items-center mb-[15px]">
                    <span className="text-[16px] font-medium line-clamp-2">
                      {record.entity.prettyName}
                    </span>
                  </div>
                  <EntityAnalyticsChart
                    slug={record.entity.slug}
                    titleClassName="font-bold"
                    priceClassName="leading-[1.4]"
                  />
                  <AddToWatchlistButton entity={record.entity}>
                    {({ addToWatchlist, isWatching }) => (
                      <span
                        className={twMerge(
                          "text-[12px] text-right",
                          isWatching ? "text-jade" : "text-smart-teal",
                          "cursor-pointer"
                        )}
                        onClick={addToWatchlist}
                      >
                        <FontAwesomeIcon icon={isWatching ? faCheck : faPlus} />{" "}
                        {isWatching ? "Added" : "Add"} to Watchlist
                      </span>
                    )}
                  </AddToWatchlistButton>
                </Popover.Body>
              </Popover>
            }
          >
            <Link
              target="_blank"
              className="underline line-clamp-1"
              href={`/entities/${record.entity.slug}`}
            >
              {value || "-"}
            </Link>
          </StickyOverlayTrigger>
        </div>
      );
    }

    return (
      <div className="w-full p-[10px] py-[8px] truncate border-0 border-b border-b-slate-200 border-solid">
        {value || "-"}
      </div>
    );
  }, []);

  const renderMarketCap = useCallback((value) => {
    return (
      <div className="w-full p-[10px] py-[8px] truncate border-0 border-b border-b-slate-200 border-solid">
        ${value || "-"}
      </div>
    );
  }, []);

  const renderHeader = useCallback(
    (value, sortKey) => {
      return (
        <div className="flex justify-between select-none">
          {value}
          {sort.includes(sortKey) && (
            <FontAwesomeIcon
              icon={!sort.includes("-") ? faCaretUp : faCaretDown}
            />
          )}
        </div>
      );
    },
    [sort]
  );

  const onSort = useCallback(
    (key) => {
      setSort(`${sort.includes("-") ? "" : "-"}${key}`);
    },
    [sort]
  );

  const valueStyle = {
    width: "90px",
    cursor: "pointer",
    textAlign: "center",
    // padding:0,
  };

  const tableColumns: any = [
    {
      key: "number",
      title: "",
      dataIndex: "number",
      render: renderNormalCols,
    },
    {
      key: "entityName",
      title: "Name",
      dataIndex: "entity.prettyName",
      style: {
        width: "200px",
      },
      render: (value: any, record: any) =>
        renderNormalCols(value, record, true),
    },
    {
      key: "country",
      title: "Country",
      dataIndex: "entity.country",
      render: renderNormalCols,
      style: {},
    },
    {
      key: "sector",
      title: "Sector",
      dataIndex: "entity.sector",
      render: renderNormalCols,
      style: {},
    },
    {
      key: "ticker",
      title: "Ticker",
      dataIndex: "entity.security",
      render: renderNormalCols,
      style: {},
    },
    {
      key: "market_cap",
      title: "Market Cap",
      dataIndex: "entity.marketCap",
      render: renderMarketCap,
      renderHeader: (value: any) => renderHeader(value, "marketCap"),
      onClick: () => {
        onSort("marketCap");
      },
      style: {
        width: "150px",
        textAlign: "center",
        cursor: "pointer",
      },
    },
    {
      key: "score",
      title: "Score",
      dataIndex: "countryAndSectorScore",
      onClick: () => {
        onSort("countryAndSectorScore");
      },
      style: valueStyle,
      render: renderCols,
      renderHeader: (value: any) =>
        renderHeader(value, "countryAndSectorScore"),
    },
    {
      key: "value",
      title: "Value",
      dataIndex: "countryAndSectorValue",
      onClick: () => {
        onSort("countryAndSectorValue");
      },
      style: valueStyle,
      render: renderCols,
      renderHeader: (value: any) =>
        renderHeader(value, "countryAndSectorValue"),
    },
    {
      key: "dividend",
      title: "Dividend",
      dataIndex: "countryAndSectorDividend",
      onClick: () => {
        onSort("countryAndSectorDividend");
      },
      style: valueStyle,
      render: renderCols,
      renderHeader: (value: any) =>
        renderHeader(value, "countryAndSectorDividend"),
    },
    {
      key: "growth",
      title: "Growth",
      dataIndex: "countryAndSectorGrowth",
      onClick: () => {
        onSort("countryAndSectorGrowth");
      },
      style: valueStyle,
      render: renderCols,
      renderHeader: (value: any) =>
        renderHeader(value, "countryAndSectorGrowth"),
    },
    {
      key: "resilience",
      title: "Resilience",
      dataIndex: "countryAndSectorResilience",
      onClick: () => {
        onSort("countryAndSectorResilience");
      },
      style: valueStyle,
      render: renderCols,
      renderHeader: (value: any) =>
        renderHeader(value, "countryAndSectorResilience"),
    },
    {
      key: "momentum",
      title: "Momentum",
      dataIndex: "countryAndSectorMomentum",
      onClick: () => {
        onSort("countryAndSectorMomentum");
      },
      style: valueStyle,
      render: (value: any, records: any) => renderCols(value, records, true),
      renderHeader: (value: any) =>
        renderHeader(value, "countryAndSectorMomentum"),
    },
  ];

  const tableData = useMemo(() => {
    return data?.reduce((rs, items, arrIndex) => {
      const pos = arrIndex * 10;

      return rs.concat(
        items?.data?.map?.((item: any, index: any) => ({
          number: pos + index + 1,
          ...item,
        })) || []
      );
    }, []);
  }, [data]);

  return (
    <div className="mt-[15px]">
      <div className="mt-[30px] px-[20px]">
        <h1 className="mb-0 font-bold text-2xl">SmartScore Screener</h1>
        <p className="text-sm">
          Screen for companies using their SmartScore, a compound score for a
          company indicating its overall outlook based on key factors.
        </p>
        {description}
      </div>

      <div className="mt-[30px]">
        <Filter
          onChange={(filters: any) => {
            setFilters(filters);
          }}
        />
        <div className="w-full overflow-scroll">
          <InfiniteScroll
            swr={{ data, mutate, isValidating, size, setSize }}
            isReachingEnd={!hasMore}
            endingIndicator={<div className={""}></div>}
          >
            <div className="mb-[30px] min-w-[1024px] overflow-x-auto">
              <Table
                headCellClassName="!border-0 !select-none !bg-white !p-[10px] !border-0 !border-b !border-b-slate-200 !border-solid"
                tableClassName="!border-0"
                cellClassName="!border-0 !p-0 !bg-white"
                columns={tableColumns}
                dataSource={tableData || []}
              />
              {!isValidating && (!tableData || tableData?.length < 1) && (
                <EmptyState type="data" />
              )}
              {isValidating && <Loader />}
            </div>
          </InfiniteScroll>
        </div>
      </div>
    </div>
  );
}
